import getClientTypeIcon from "@utils/getClientTypeIcon"

import { Avatar } from "@atoms"

import {
    CreditLimitBadge,
    IndustryTypeBadge,
    LabelsBadges,
    LastModifiedBadge,
    ServiceLocationsBadge,
    TermsBadge,
} from "@molecules/ObjectCard/Attributes/AttributeBadges"
import ObjectCard from "@molecules/ObjectCard/ObjectCard"
import { shouldShowAttribute } from "@molecules/ObjectCard/ObjectCards/ObjectCards.utils"
import ObjectStatusBadge from "@molecules/ObjectStatusBadge/ObjectStatusBadge"

import { ClientCardProps } from "./ClientCard.types"

export default function ClientCard(props: ClientCardProps) {
    const { client, displayedAttributes, onClick, isLoading = false, className, ...errorProps } = props

    const showCreditLimit = shouldShowAttribute(displayedAttributes, "credit_limit")
    const showServiceLocations = shouldShowAttribute(displayedAttributes, "service_locations_count")
    const showTerms =
        shouldShowAttribute(displayedAttributes, "default_invoice_net") && client.default_invoice_net != null
    const showIndustryType =
        shouldShowAttribute(displayedAttributes, "industry_type_label") && Boolean(client.industry_type_label)
    const showLastModified =
        shouldShowAttribute(displayedAttributes, "last_modified_at") && Boolean(client.last_modified_at)
    const showLabels = shouldShowAttribute(displayedAttributes, "labels") && client.labels.length > 0

    const hasAttributes =
        showCreditLimit || showServiceLocations || showTerms || showIndustryType || showLastModified || showLabels

    return (
        <ObjectCard onClick={onClick} isLoading={isLoading} className={className} {...errorProps}>
            <ObjectCard.Header
                id={client.client_type_label}
                statusBadge={<ObjectStatusBadge objectType="client" status={client.status_label} size="md" />}
            />
            <ObjectCard.Body
                avatar={<Avatar variant="object" icon={getClientTypeIcon(client.client_type_label)} size="xl" />}
                primaryText={client.name}
                secondaryText={client.billing_address_formatted ?? undefined}
                secondaryTextPlaceholder="No billing address"
                secondaryTextTooltip={client.billing_address_formatted_multiline ?? undefined}
            />
            {(isLoading || hasAttributes) && (
                <ObjectCard.Attributes>
                    {showCreditLimit && <CreditLimitBadge creditLimit={client.credit_limit} />}
                    {showServiceLocations && <ServiceLocationsBadge count={client.service_locations_count} />}
                    {showTerms && (
                        <TermsBadge
                            variant="fixed"
                            netDays={Number(client.default_invoice_net)}
                            label="Default invoice terms"
                        />
                    )}
                    {showIndustryType && <IndustryTypeBadge industryType={client.industry_type_label} />}
                    {showLastModified && <LastModifiedBadge date={client.last_modified_at} />}
                    {showLabels && <LabelsBadges labels={client.labels} />}
                </ObjectCard.Attributes>
            )}
        </ObjectCard>
    )
}
